/**
 * Training Session Operations
 * 
 * Provides CRUD operations for training sessions with proper offline handling,
 * conflict detection, and sync queue management.
 */

import { BaseOperations } from './shared';
import { STORES } from '../schema';
import type { TrainingSessionOffline } from '../schema';

/**
 * Training Sessions Operations
 */
export class TrainingSessionOperations extends BaseOperations<TrainingSessionOffline> {
  constructor() {
    super(STORES.TRAINING_SESSIONS);
  }

  /**
   * Get sessions for a specific user
   */
  async getByUserId(userId: string): Promise<TrainingSessionOffline[]> {
    // Return raw data without sorting - sorting will be done after conversion
    return this.queryByIndex('by_user_id', userId);
  }

  /**
   * Get sessions for a user within a date range
   */
  async getByUserAndDateRange(userId: string, startDate: string, endDate: string): Promise<TrainingSessionOffline[]> {
    const userSessions = await this.getByUserId(userId);
    return userSessions.filter(session => 
      session.date >= startDate && session.date <= endDate
    );
  }

  /**
   * Get recent sessions (last 90 days)
   */
  async getRecentSessions(userId: string, days = 90): Promise<TrainingSessionOffline[]> {
    const endDate = new Date().toISOString().split('T')[0];
    const startDate = new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
    return this.getByUserAndDateRange(userId, startDate, endDate);
  }

  /**
   * Get sessions by type
   */
  async getBySessionType(sessionType: string): Promise<TrainingSessionOffline[]> {
    return this.queryByIndex('by_session_type', sessionType);
  }
}

// Export singleton instance
export const trainingSessionOps = new TrainingSessionOperations();
